import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getTemplateBySourceFile,
  patchWorkflowTemplateField,
  type TemplateDocumentDTO,
} from '../api/workflowApi';
import { toast } from './use-toast';

export interface TemplateLink {
  /** Display name to pass to PdfSigningViewer's linkedTemplateName prop. */
  templateName: string;
  /** Persists a moved field's bbox to the linked template. */
  onSaveFieldToTemplate: (input: {
    fieldId: string;
    rect: { x: number; y: number; width: number; height: number };
  }) => Promise<void>;
}

/**
 * V5 — look up the saved TemplateDocument that the given fileId
 * resolves to (matched server-side by contentHash). When a template
 * is found, returns a stable `TemplateLink` whose handler closes
 * over (workflowId, templateId) so callers don't need to thread
 * those through.
 *
 * On no-match, returns null. Re-fires when fileId changes; cancels
 * stale requests so rapid file switches don't race.
 */
export function useTemplateForFile(fileId: string | null | undefined): TemplateLink | null {
  const [template, setTemplate] = useState<TemplateDocumentDTO | null>(null);
  // Tracks the most-recently-requested fileId so a late response from
  // a prior fileId can't overwrite the state of a newer one.
  const lastRequestedFileId = useRef<string | null>(null);

  useEffect(() => {
    if (!fileId) {
      setTemplate(null);
      lastRequestedFileId.current = null;
      return;
    }
    let cancelled = false;
    lastRequestedFileId.current = fileId;
    getTemplateBySourceFile(fileId)
      .then((result) => {
        if (cancelled) return;
        if (lastRequestedFileId.current !== fileId) return;
        setTemplate(result);
      })
      .catch((err) => {
        if (cancelled) return;
        // Lookup failure is non-fatal — banner just stays hidden.
        console.warn('[useTemplateForFile] lookup failed', err);
        setTemplate(null);
      });
    return () => { cancelled = true; };
  }, [fileId]);

  return useMemo<TemplateLink | null>(() => {
    if (!template) return null;
    const { id: templateId, workflowId, name: templateName } = template;
    return {
      templateName,
      onSaveFieldToTemplate: async ({ fieldId, rect }) => {
        try {
          await patchWorkflowTemplateField(workflowId, templateId, fieldId, rect);
          toast({
            title: 'Template updated',
            description: `Future docs using "${templateName}" will use this position.`,
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Save failed';
          toast({
            title: 'Couldn\'t update template',
            description: message,
            variant: 'destructive',
          });
          // Re-throw so the viewer's handler can keep the local override
          // visible — the user's drag isn't lost.
          throw err;
        }
      },
    };
  }, [template]);
}
